import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import {
  AdvancedTargetingDefinition,
  Quota,
  SurveyLocaleData,
  SurveyMetadataFormState,
} from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import {
  SurveyCreationActions,
  SurveyCreationNavbarActions,
} from './audience/survey-creation-audience.actions';
import { SurveyCreationReferenceActions } from './reference/survey-creation-reference.actions';

@Injectable({ providedIn: 'root' })
export class SurveyCreationActionsFacade {
  private readonly _store = inject(Store);

  public init(surveyId: Nullable<number>): void {
    this._store.dispatch(SurveyCreationActions.init({ surveyId }));
  }

  public createStandardSurvey(): void {
    this._store.dispatch(SurveyCreationActions.createStandardSurvey());
  }

  public updateMetadata(metadata: SurveyMetadataFormState): void {
    this._store.dispatch(SurveyCreationActions.updateMetadata({ metadata }));
  }

  public saveSurvey(): void {
    this._store.dispatch(SurveyCreationActions.saveSurvey());
  }

  public reset(): void {
    this._store.dispatch(SurveyCreationActions.reset());
  }

  public toggleLocationQualificationOption(
    qualificationCode: number,
    optionId: string,
  ): void {
    this._store.dispatch(
      SurveyCreationActions.toggleLocationQualificationOption({
        qualificationCode,
        optionId,
      }),
    );
  }

  public clearLocationQualificationSelections(): void {
    this._store.dispatch(
      SurveyCreationActions.clearLocationQualificationSelections(),
    );
  }

  public loadQualifications(localeData: SurveyLocaleData[]): void {
    for (const eachLocaleData of localeData) {
      this._store.dispatch(
        SurveyCreationReferenceActions.loadQualifications(eachLocaleData),
      );
    }
  }

  public loadQuestionCategories(): void {
    this._store.dispatch(
      SurveyCreationReferenceActions.loadQuestionCategories(),
    );
  }

  public loadQuestions(search: string, categoryIds: number[]): void {
    this._store.dispatch(
      SurveyCreationReferenceActions.loadQuestions({ search, categoryIds }),
    );
  }

  public toggleManualProcessing(companyId: number, enabled: boolean): void {
    this._store.dispatch(
      SurveyCreationNavbarActions.toggleManualProcessing({
        companyId,
        enabled,
      }),
    );
  }

  public recalculateFeasibility(): void {
    this._store.dispatch(SurveyCreationNavbarActions.recalculateFeasibility());
  }

  public applyAdvancedTargetingDefinition(
    definition: AdvancedTargetingDefinition,
  ): void {
    this._store.dispatch(
      SurveyCreationActions.applyAdvancedTargetingDefinition({ definition }),
    );
  }

  public removeAdvancedTargetingDefinition(
    definition: AdvancedTargetingDefinition,
  ): void {
    this._store.dispatch(
      SurveyCreationActions.removeAdvancedTargetingDefinition({ definition }),
    );
  }

  public applyAdvancedTargetingQuotas(
    attributeId: string,
    quotas: Quota[],
  ): void {
    this._store.dispatch(
      SurveyCreationActions.applyAdvancedTargetingQuotas({
        attributeId,
        quotas,
      }),
    );
  }
}
